import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug } from '@/utils/utils';
import { AttachInternals, Component, Element, Event, EventEmitter, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';
import { ThemeConfig } from '@nylas/core';
/**
 * The `nylas-event-capacity` component is a UI component that allows users to set the event capacity for the scheduler configuration.
 *
 * @part necapacity - The nylas-event-capacity container
 * @part necapacity__body - The body of the event capacity section
 * @part necapacity__header - The header of the event capacity section
 * @part necapacity__drawer-toggle--container - The card's drawer toggle container
 * @part necapacity__input-numberfield - The event capacity input numberfield
 */
@Component({
  tag: 'nylas-event-capacity',
  styleUrl: 'nylas-event-capacity.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasEventCapacity {
  @Element() host!: HTMLNylasEventCapacityElement;

  /**
   * @standalone
   * The name of the component
   */
  @Prop() name: string = 'event-capacity';
  /**
   * @standalone
   * The default event capacity
   */
  @Prop() capacity?: number;

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * The current event capacity state
   */
  @State() selectedEventCapacity!: number;

  @State() error: string = '';

  /**
   * This event is fired when the event capacity value changes
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
    valueChanged?: (event: CustomEvent<{ value: string; name: string }>) => void;
  }>;

  // Lifecycle methods
  connectedCallback() {
    debug('nylas-event-capacity', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {
    debug('nylas-event-capacity', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-event-capacity', 'componentWillLoad');
    // See comment in the @Watch('name') decorator for more information.
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-event-capacity', 'componentDidLoad');
    this.selectedEventCapacity = this.capacity || 10;
    debug('selectedEventCapacity', this.selectedEventCapacity);
    this.internals.setFormValue(this.selectedEventCapacity.toString(), this.name);
  }

  componentWillUpdate() {
    debug('nylas-event-capacity', 'componentWillUpdate');
  }

  componentDidUpdate() {
    debug('nylas-event-capacity', 'componentDidUpdate');
  }

  componentWillRender() {
    debug('nylas-event-capacity', 'componentWillRender');
  }

  componentDidRender() {
    debug('nylas-event-capacity', 'componentDidRender');
  }

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-event-capacity', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  @Listen('nylasFormInputChanged')
  async nylasFormInputChangeHandler(event: CustomEvent<{ value: string; name: string }>) {
    const { name, value } = event.detail;

    if (name === 'event-capacity') {
      const parsedValue = parseInt(value);
      if (parsedValue < 1 || parsedValue > 500 || isNaN(parsedValue)) {
        this.error = i18next.t('fixFormErrors');
        this.internals.setValidity({ customError: true }, this.error);
        this.valueChanged.emit({ value: value.toString(), name: this.name });
        return;
      }
      this.error = '';
      this.internals.setValidity({ customError: false }, '');
      this.selectedEventCapacity = parseInt(value);
      this.internals.setFormValue(value.toString(), this.name);
      this.valueChanged.emit({ value: value.toString(), name: this.name });
    }
  }

  @RegisterComponent<NylasEventCapacity, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-event-capacity',
    stateToProps: new Map([
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    eventToProps: {
      valueChanged: async (
        event: CustomEvent<{ value: string; name: string; valueChanged?: (event: CustomEvent<{ value: string; name: string }>) => void }>,
        _nylasSchedulerConfigConnector: NylasSchedulerConfigConnector,
      ) => {
        const { valueChanged } = event.detail;
        // If a handler is passed, call it.
        if (valueChanged) {
          valueChanged(event);
        }
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    const isComponentReady = typeof this.selectedEventCapacity !== 'undefined';
    return (
      <Host>
        <div class="nylas-event-capacity" part="necapacity">
          <label htmlFor="event-capacity">
            <span>
              {i18next.t('nylasEventCapacity.headerTitle')}
              <span class="required">*</span>
            </span>
            <tooltip-component>
              <info-icon slot="tooltip-icon" />
              <span slot="tooltip-content">{i18next.t('nylasEventCapacity.tooltip.desc')}</span>
            </tooltip-component>
          </label>
          {isComponentReady && (
            <div class="nylas-event-capacity__wrapper">
              <input-component
                name="event-capacity"
                id="event-capacity"
                type="number"
                pattern={/^[0-9]*$/}
                patternError={i18next.t('invalidInputFormat', { field: 'Event capacity' })}
                minValue={1}
                maxValue={500}
                required={false}
                exportparts="ic_input: necapacity__input-textfield"
                defaultValue={typeof this.selectedEventCapacity === 'number' ? this.selectedEventCapacity.toString() : '10'}
                themeConfig={this.themeConfig}
              />
              <span class={this.error ? 'attendees error' : 'attendees'}>{i18next.t('nylasEventCapacity.attendees')}</span>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
